import { useMutation } from "@tanstack/react-query";
import { type Dispatch, type SetStateAction, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { backendService } from "../services/backend.service";
import type { AnnieFeedbackRequest, AnnieFeedbackResponse, AnnieFeedbackState } from "../utils/annieStream";
import { extractAnnieError } from "./annieErrors";
import type { AnnieChatItem } from "./useAnnie";
import { useErrorSnackbar } from "./useErrorSnackbar";

interface AnnieFeedbackMutationVariables {
	itemId: string;
	conversationId: number;
	replacement: AnnieFeedbackRequest;
	priorFeedback: AnnieFeedbackState;
}

export type SaveAnnieFeedback = (itemId: string, conversationId: number, replacement: AnnieFeedbackRequest) => Promise<boolean>;

const analyticsFields = ({ conversationId, replacement }: AnnieFeedbackMutationVariables, departmentId: string) => ({
	department_id: departmentId,
	conversation_id: conversationId,
	rating: replacement.rating,
	reason: replacement.reason,
});

export const useAnnieFeedback = (items: AnnieChatItem[], setItems: Dispatch<SetStateAction<AnnieChatItem[]>>): SaveAnnieFeedback => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();
	const { t } = useTranslation();
	const departmentId = chosenDepartment.department_id;
	const { mutateAsync: submitFeedback } = useMutation<AnnieFeedbackResponse, unknown, AnnieFeedbackMutationVariables>({
		mutationFn: async ({ conversationId, replacement }) => (await backendService.updateAnnieAnswerFeedback(conversationId, replacement)).data,
		onMutate: (variables) => {
			setItems((previous) =>
				previous.map((item) =>
					item.id === variables.itemId && item.kind === "assistant"
						? {
								...item,
								feedback: {
									rating: variables.replacement.rating,
									reason: variables.replacement.reason ?? null,
									comment: variables.replacement.comment ?? null,
									updated_at: variables.priorFeedback.updated_at,
								},
								isSavingFeedback: true,
							}
						: item,
				),
			);
			track("annie_feedback_submit_start", analyticsFields(variables, departmentId));
		},
		onSuccess: (response, variables) => {
			setItems((previous) =>
				previous.map((item) =>
					item.id === variables.itemId && item.kind === "assistant" ? { ...item, feedback: response.feedback, isSavingFeedback: false } : item,
				),
			);
			track("annie_feedback_submit_success", analyticsFields(variables, departmentId));
		},
		onError: (error, variables) => {
			setItems((previous) =>
				previous.map((item) =>
					item.id === variables.itemId && item.kind === "assistant" ? { ...item, feedback: variables.priorFeedback, isSavingFeedback: false } : item,
				),
			);
			const analytics = analyticsFields(variables, departmentId);
			const { status, key } = extractAnnieError(error);
			trackError("annie_feedback_submit_error", { ...analytics, status, message_key: key });
			// Do not pass the Axios error: its request config contains the private feedback comment.
			showErrorSnackbar(t("annie.feedback.save.failed"), analytics);
		},
	});

	return useCallback(
		async (itemId: string, conversationId: number, replacement: AnnieFeedbackRequest): Promise<boolean> => {
			const target = items.find((item) => item.id === itemId && item.kind === "assistant");
			if (target?.kind !== "assistant" || target.feedback === undefined) throw new Error("cannot rate an ineligible Annie answer");
			try {
				await submitFeedback({ itemId, conversationId, replacement, priorFeedback: target.feedback });
				return true;
			} catch {
				// useMutation.onError already restores state, tracks the failure, and surfaces the snackbar.
				return false;
			}
		},
		[items, submitFeedback],
	);
};
